/**
 * Readable Text Color Utility (issue #745)
 *
 * `TagBadge` renders an arbitrary `tag.color` as a chip background. A fixed
 * white foreground fails WCAG 1.4.3 (4.5:1 contrast) against light/mid
 * lightness fills such as the Cloud Migration 2030 tag palette (`#DCE775`,
 * `#F9A825`, `#B0BEC5`, etc). `readableTextColor` picks whichever of a dark
 * or light foreground yields the higher WCAG contrast ratio against a given
 * background, using the standard WCAG 2.1 relative-luminance formula.
 */

/** Dark foreground used against light backgrounds. */
export const DARK_TEXT_COLOR = '#212121';

/** Light foreground used against dark backgrounds. */
export const LIGHT_TEXT_COLOR = '#ffffff';

/**
 * Fallback chip background used when a `Tag` has no `color` set. Shared with
 * `readableTextColor` (via `FALLBACK_TAG_COLOR`) so the fallback foreground
 * stays computed from the same single source of truth as the fallback
 * background, rather than each module hardcoding its own copy.
 */
export const FALLBACK_TAG_COLOR = '#6B7280';

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * Parse a hex color string (`#rgb`, `#rrggbb`, with or without the leading
 * `#`) into 0-255 RGB channels. Returns `null` for malformed input (wrong
 * length, non-hex characters, empty/missing string) so callers can fall back
 * safely instead of throwing.
 */
function parseHexColor(hex: string): Rgb | null {
  const normalized = hex.trim().replace(/^#/, '');

  if (normalized.length === 3) {
    const expanded = normalized
      .split('')
      .map((char) => char + char)
      .join('');
    return parseHexColor(`#${expanded}`);
  }

  if (normalized.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

/** Converts an 8-bit sRGB channel value to its linear-light form (WCAG 2.1). */
function linearizeChannel(channel8Bit: number): number {
  const channel = channel8Bit / 255;
  return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
}

/** WCAG 2.1 relative luminance of an RGB color (0 = black, 1 = white). */
function relativeLuminance({ r, g, b }: Rgb): number {
  return 0.2126 * linearizeChannel(r) + 0.7152 * linearizeChannel(g) + 0.0722 * linearizeChannel(b);
}

/** WCAG 2.1 contrast ratio between two relative luminances (always >= 1). */
function contrastRatio(luminanceA: number, luminanceB: number): number {
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
}

const DARK_TEXT_LUMINANCE = relativeLuminance(parseHexColor(DARK_TEXT_COLOR) as Rgb);
const LIGHT_TEXT_LUMINANCE = relativeLuminance(parseHexColor(LIGHT_TEXT_COLOR) as Rgb);

/**
 * Picks the foreground color — {@link DARK_TEXT_COLOR} or
 * {@link LIGHT_TEXT_COLOR} — that gives the higher WCAG contrast ratio
 * against `backgroundHex`. Malformed, short (`#abc`), or missing hex input
 * falls back to computing against {@link FALLBACK_TAG_COLOR} so the result
 * is always a valid, readable foreground rather than a thrown error.
 *
 * @param backgroundHex - Chip background color, e.g. `'#DCE775'`.
 * @returns `'#212121'` or `'#ffffff'`, whichever is more readable.
 */
export function readableTextColor(
  backgroundHex: string | null | undefined
): typeof DARK_TEXT_COLOR | typeof LIGHT_TEXT_COLOR {
  const rgb =
    (backgroundHex ? parseHexColor(backgroundHex) : null) ?? parseHexColor(FALLBACK_TAG_COLOR);

  // parseHexColor(FALLBACK_TAG_COLOR) is always valid — FALLBACK_TAG_COLOR is
  // a well-formed constant — but guard for type-safety without an unsafe cast.
  if (!rgb) {
    return LIGHT_TEXT_COLOR;
  }

  const backgroundLuminance = relativeLuminance(rgb);
  const contrastWithDark = contrastRatio(backgroundLuminance, DARK_TEXT_LUMINANCE);
  const contrastWithLight = contrastRatio(backgroundLuminance, LIGHT_TEXT_LUMINANCE);

  return contrastWithDark >= contrastWithLight ? DARK_TEXT_COLOR : LIGHT_TEXT_COLOR;
}
